import React from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { cn } from '@/lib/utils';

export type ProgressStatus = 
  | 'visit'
  | 'presentation'
  | 'trial'
  | 'negotiation'
  | 'paid-client'
  | 'rejected'
  | 'mou-signed';

interface ProgressOption {
  value: ProgressStatus;
  label: string;
  emoji: string;
  color: string;
  bgColor: string;
}

const progressOptions: ProgressOption[] = [
  {
    value: 'visit',
    label: 'Visit',
    emoji: '🏠',
    color: 'text-primary',
    bgColor: 'bg-primary/10'
  },
  {
    value: 'presentation',
    label: 'Presentation',
    emoji: '📊',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50'
  },
  {
    value: 'trial',
    label: 'Trial',
    emoji: '🧪',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50'
  },
  // {
  //   value: 'negotiation',
  //   label: 'Negotiation',
  //   emoji: '🤝',
  //   color: 'text-yellow-600',
  //   bgColor: 'bg-yellow-50'
  // },
  // {
  //   value: 'paid-client',
  //   label: 'Paid Client',
  //   emoji: '💰',
  //   color: 'text-green-600',
  //   bgColor: 'bg-green-50'
  // },
  {
    value: 'rejected',
    label: 'Rejected',
    emoji: '❌',
    color: 'text-red-600',
    bgColor: 'bg-red-50'
  },
  {
    value: 'mou-signed',
    label: 'MOU Signed',
    emoji: '📝',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50'
  }
];

interface ProgressDropdownProps {
  value: ProgressStatus | null;
  onChange: (value: ProgressStatus) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const ProgressDropdown: React.FC<ProgressDropdownProps> = ({
  value,
  onChange,
  placeholder = "Select progress status",
  disabled = false,
  className = ""
}) => {
  const selectedOption = value ? progressOptions.find(option => option.value === value) : null;

  return (
    <div className={cn("relative", className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="w-full px-4 py-3 h-auto justify-between"
            disabled={disabled}
          >
            <div className="flex items-center space-x-3">
              {selectedOption ? (
                <>
                  <span className="text-lg">{selectedOption.emoji}</span>
                  <div className="flex flex-col text-left">
                    <span className={cn("font-medium", selectedOption.color)}>
                      {selectedOption.label}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Progress Status
                    </span>
                  </div>
                </>
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
            </div>
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuPortal>
          <DropdownMenuContent className="w-[280px] z-[80]" align="start" collisionPadding={10}>
            <DropdownMenuLabel>Select Progress Status</DropdownMenuLabel>
            {progressOptions.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onClick={() => onChange(option.value)}
                className={cn(
                  "flex items-center justify-between px-4 py-3 cursor-pointer",
                  value === option.value && `${option.bgColor} ${option.color}`
                )}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-lg">{option.emoji}</span>
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {option.label}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {getProgressDescription(option.value)}
                    </span>
                  </div>
                </div>
                {value === option.value && (
                  <Check className={cn("w-5 h-5", option.color)} />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>
    </div>
  );
};

// Helper function to get progress descriptions
const getProgressDescription = (status: ProgressStatus): string => {
  const descriptions: Record<ProgressStatus, string> = {
    'visit': 'Customer visit conducted',
    'presentation': 'Presenting product/service',
    'trial': 'Trial period active',
    'negotiation': 'Discussing terms',
    'paid-client': 'Successfully converted',
    'rejected': 'Declined offer',
    'mou-signed': 'Agreement finalized'
  };
  
  return descriptions[status];
};

export default ProgressDropdown;